// Error handling utilities for browser extension compatibility and runtime errors

/**
 * Check if an error is related to browser extensions
 * @param {Error|string} error - The error to check
 * @returns {boolean} - True if the error is extension-related
 */
export const isExtensionError = (error) => {
  const message = typeof error === 'string' ? error : error?.message || '';
  
  return message.includes('runtime.lastError') ||
         message.includes('message port closed') ||
         message.includes('Extension context invalidated') ||
         message.includes('chrome-extension://') ||
         message.includes('moz-extension://') ||
         message.includes('safari-extension://') ||
         message.includes('listener indicated an asynchronous response');
};

/**
 * Safe wrapper for Chrome extension API calls
 * @param {Function} callback - Function to execute
 * @param {*} fallback - Fallback value if extension API fails
 * @returns {*} - Result of callback or fallback
 */
export const safeExtensionCall = (callback, fallback = null) => {
  try {
    if (typeof chrome === 'undefined' || !chrome.runtime) {
      return fallback;
    }
    
    const result = callback();
    
    // Check for runtime errors after the call
    if (chrome.runtime.lastError) {
      console.debug('Chrome extension error (suppressed):', chrome.runtime.lastError.message);
      return fallback;
    }
    
    return result;
  } catch (error) {
    if (isExtensionError(error)) {
      console.debug('Extension error suppressed:', error.message);
      return fallback;
    }
    throw error;
  }
};

/**
 * Enhanced console error handler that filters extension errors
 * @param {string} level - Console level (log, warn, error)
 * @param {*} args - Arguments to log
 */
export const safeConsole = (level, ...args) => {
  const message = args.join(' ');
  
  if (isExtensionError(message)) {
    console.debug('Extension-related message (filtered):', message);
    return;
  }
  
  console[level](...args);
};

/**
 * Wrapper for async operations that might be interrupted by extensions
 * @param {Promise} promise - Promise to wrap
 * @param {*} fallback - Fallback value on extension errors
 * @returns {Promise} - Wrapped promise
 */
export const extensionSafePromise = async (promise, fallback = null) => {
  try {
    return await promise;
  } catch (error) {
    if (isExtensionError(error)) {
      console.debug('Extension promise error (handled):', error.message);
      return fallback;
    }
    throw error;
  }
};

/**
 * Initialize global error handlers for extension compatibility
 */
export const initializeErrorHandlers = () => {
  // Override console methods to filter extension errors
  const originalError = console.error;
  const originalWarn = console.warn;
  
  console.error = (...args) => {
    const message = args.join(' ');
    if (!isExtensionError(message)) {
      originalError.apply(console, args);
    }
  };
  
  console.warn = (...args) => {
    const message = args.join(' ');
    if (!isExtensionError(message)) {
      originalWarn.apply(console, args);
    }
  };
  
  // Handle uncaught exceptions
  window.addEventListener('error', (event) => {
    if (isExtensionError(event.error || event.message)) {
      event.preventDefault();
      return false;
    }
  });
  
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (isExtensionError(event.reason)) {
      event.preventDefault();
      return false;
    }
  });
  
  console.log('Extension-compatible error handlers initialized');
};

export default {
  isExtensionError,
  safeExtensionCall,
  safeConsole,
  extensionSafePromise,
  initializeErrorHandlers
};
